import React, { useEffect, useState } from 'react';
import './index.scss';
import {
    NavigationBar,
    IconChevronLeftRegular,
    Text2,
    ThemeVariant,
    Text6,
    Text3,
    ButtonFixedFooterLayout,
    ButtonPrimary,
    skinVars,
    useWindowSize,
    Spinner,
} from '@telefonica/mistica';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrackingEvents } from '../../../../../../utils/dataTransformation/tracking';
import { formatMobileNumber } from '../../../../../../utils/dataTransformation/unitsConversion';
import { isWebviewAndroid, isWebviewIOS } from '../../../../../../utils/navigation/controlDevices';
import navigateParams from '../../../../../../utils/navigation/navigateParams';
import { updateAppointment } from '../../../../../../services/OrderService';
import { setInfoError } from '../../../../../../utils/dataTransformation/errorMesssages';
import { AppointmentInfo, Slot } from '../../../../../../models/Orders';
import { retrieveModifyAppointmentInfo } from '../../../appointmentStorage';
import { getConfirmationText } from '../../../slotUtils';
import BreadCrumbsWrapper from '../../../../../../components/BreadcrumbsWrapper';

export const ModifyAppointmentConfirm: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { trackingScreenView } = new TrackingEvents();
    const [orderDescription, setOrderDescription] = useState<string>('');
    const [appointmentInfo, setAppointmentInfo] = useState<AppointmentInfo>();
    const [selectedSlot, setSelectedSlot] = useState<Slot>();
    const [params] = useSearchParams();
    const size = useWindowSize();
    const [loading, setLoading] = useState<boolean>(false);

    const sendToNative = (param: any, description: any, type: any, err?: any, title?: string) => {
        const url = param;
        const isSuccess = description === 'SUCCESS';
        const queryParams = {
            title: isSuccess ? t('rehabilitationLinesInfoFeedbackTitle') : t('profileFeedbackErrorTitle'),
            subtitle: isSuccess ? t('successFeedbackDescriptionConfirmDate') : t('errorFeedbackDescriptionConfirmDate'),
            state: description,
            errorCode: err ? `${t('codeError')}: #${err.id}` : '',
        };
        if (navigateParams(type, url, queryParams, {}, title)) {
            navigate(url, {
                state: {
                    stateFeedback: description,
                    err: err,
                },
            });
        }
    };

    const confirmAppointmentUpdate = () => {
        setLoading(true);
        updateAppointment(appointmentInfo!.workId, appointmentInfo!.appointmentId, selectedSlot!)
            .then(() => {
                setLoading(false);
                sendToNative('/order-detail/modify-appointment/feedback', 'SUCCESS', 'feedback-success');
            })
            .catch((error) => {
                sendToNative(
                    '/order-detail/modify-appointment/feedback',
                    'ERROR',
                    'feedback-error',
                    setInfoError(error),
                );
            });
    };

    useEffect(() => {
        const { orderDescription, appointmentInfo, selectedSlot } = retrieveModifyAppointmentInfo();
        setOrderDescription(orderDescription);
        setAppointmentInfo(appointmentInfo);
        setSelectedSlot(selectedSlot);
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    return (
        <>
            {loading ? (
                <div className='c-spinner'>
                    <Spinner size={24} />
                    <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
                </div>
            ) : (
                <div className='confirmAppointmentModification-page'>
                    {selectedSlot && (
                        <>
                            <div
                                className='confirmAppointmentModification-page__header'
                                style={{ background: skinVars.colors.backgroundBrand }}
                            >
                                {!(isWebviewAndroid || isWebviewIOS) && (
                                    <>
                                        <div className='confirmAppointmentModification-page__header-navigationBar'>
                                            <NavigationBar
                                                isInverse={true}
                                                onBack={() => {
                                                    navigate(-1);
                                                }}
                                                title={t('modifyAppointment')}
                                            />
                                        </div>
                                        <div className='confirmAppointmentModification-page__header-return'>
                                            <div
                                                className='confirmAppointmentModification-page__header-return-cursor'
                                                onClick={() => {
                                                    navigate(-1);
                                                }}
                                            >
                                                <div className='confirmAppointmentModification-page__header-return-cursor-icon'>
                                                    <IconChevronLeftRegular
                                                        color={skinVars.colors.appBarBackground}
                                                        size={20}
                                                    />
                                                </div>
                                                <Text2 medium color={skinVars.colors.appBarBackground}>
                                                    {t('returnResponsive')}
                                                </Text2>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className='confirmAppointmentModification-page__header-breadcrumbs'>
                                    <ThemeVariant isInverse>
                                        <BreadCrumbsWrapper
                                            title={t('modifyAppointment')}
                                            breadcrumbs={[
                                                { title: t('profileOrders'), url: '/profile/orders' },
                                                {
                                                    title: t('orderDetail'),
                                                    url: `/order-detail?id=${params.get('id')}`,
                                                },
                                            ]}
                                        />
                                    </ThemeVariant>
                                </div>
                                <div className='confirmAppointmentModification-page__header-product'>
                                    <Text6 color={skinVars.colors.textPrimaryInverse}>{orderDescription}</Text6>
                                </div>
                            </div>
                            {size.width >= 1024 ? (
                                <div className='confirmAppointmentModification-page__content'>
                                    <div className='confirmAppointmentModification-page__content-dataConfirm'>
                                        <Text3 regular>{t('confirmAppointmentData')}</Text3>
                                    </div>
                                    <div className='confirmAppointmentModification-page__content-section'>
                                        <div className='confirmAppointmentModification-page__content-section-title'>
                                            <Text2 regular>{t('date')}</Text2>
                                        </div>
                                        <Text2 regular color={skinVars.colors.textSecondary}>
                                            {getConfirmationText(selectedSlot)}
                                        </Text2>
                                    </div>
                                    <div className='confirmAppointmentModification-page__content-section'>
                                        <div className='confirmAppointmentModification-page__content-section-title'>
                                            <Text2 regular>{t('contactNumber')}</Text2>
                                        </div>
                                        <Text2 regular color={skinVars.colors.textSecondary}>
                                            {appointmentInfo!.contactInfo
                                                .map((contact) => formatMobileNumber(contact.number))
                                                .join(', ')}
                                        </Text2>
                                    </div>
                                    <div className='confirmAppointmentModification-page__content-section'>
                                        <div className='confirmAppointmentModification-page__content-section-title'>
                                            <Text2 regular>{t('location')}</Text2>
                                        </div>
                                        <Text2 regular color={skinVars.colors.textSecondary}>
                                            {appointmentInfo!.address}
                                        </Text2>
                                    </div>
                                    <div className='confirmAppointmentModification-page__content-button'>
                                        <ButtonPrimary
                                            onPress={() => {
                                                confirmAppointmentUpdate();
                                            }}
                                        >
                                            {t('confirmDate')}
                                        </ButtonPrimary>
                                    </div>
                                </div>
                            ) : (
                                <div className='confirmAppointmentModification-page__content'>
                                    <ButtonFixedFooterLayout
                                        button={
                                            <ButtonPrimary
                                                onPress={() => {
                                                    confirmAppointmentUpdate();
                                                }}
                                            >
                                                {t('confirmDate')}
                                            </ButtonPrimary>
                                        }
                                    >
                                        <div className='confirmAppointmentModification-page__content-dataConfirm'>
                                            <Text3 regular>{t('confirmAppointmentData')}</Text3>
                                        </div>
                                        <div className='confirmAppointmentModification-page__content-section'>
                                            <div className='confirmAppointmentModification-page__content-section-title'>
                                                <Text2 regular>{t('date')}</Text2>
                                            </div>
                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                {getConfirmationText(selectedSlot)}
                                            </Text2>
                                        </div>
                                        <div className='confirmAppointmentModification-page__content-section'>
                                            <div className='confirmAppointmentModification-page__content-section-title'>
                                                <Text2 regular>{t('contactNumber')}</Text2>
                                            </div>
                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                {appointmentInfo!.contactInfo
                                                    .map((contact) => formatMobileNumber(contact.number))
                                                    .join(', ')}
                                            </Text2>
                                        </div>
                                        <div className='confirmAppointmentModification-page__content-section'>
                                            <div className='confirmAppointmentModification-page__content-section-title'>
                                                <Text2 regular>{t('location')}</Text2>
                                            </div>
                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                {appointmentInfo!.address}
                                            </Text2>
                                        </div>
                                    </ButtonFixedFooterLayout>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default ModifyAppointmentConfirm;
